import React, { FC, useState } from "react"
import { useNavigate, useLocation } from "react-router-dom"
import { useAuth } from "../../Context/AuthContext"

interface SidebarProps {
  activeTab?: string
  totalBookings?: number
  callScheduled?: number
}

const Sidebar: FC<SidebarProps> = ({ 
  activeTab = 'all',
  totalBookings = 20,
  callScheduled = 3
}) => {
  const navigate = useNavigate()
  const location = useLocation()
  const { user, logout } = useAuth()
  const [isLoggingOut, setIsLoggingOut] = useState(false)

  const handleLogout = async () => {
    if (isLoggingOut) return
    setIsLoggingOut(true)
    try {
      await logout()
      navigate('/login', { replace: true })
    } catch (error) {
      console.error('ログアウトに失敗しました', error)
    } finally {
      setIsLoggingOut(false)
    }
  }

  // 現在のパスからアクティブタブを判定
  // basenameが/mainなので、location.pathnameは/mainから始まる
  const isActive = (tab: string) => {
    if (tab === 'all') {
      // /main または /main/ で、かつクエリパラメータにstatus=calloutがない場合
      const isMainPath = location.pathname === '/main' || location.pathname === '/main/'
      return isMainPath && !location.search.includes('status=callout')
    }
    if (tab === 'call_scheduled') {
      return location.search.includes('status=callout')
    }
    return false
  }

  const handleNavigate = (path: string) => {
    // basenameが/mainなので、ルート内でのパスは/から始める
    navigate(path)
  }

  const handleAllBookingsClick = (e: React.MouseEvent) => {
    e.preventDefault()
    // 予約一覧を表示するためにstatus=allを設定
    handleNavigate('/?status=all')
  }

  return (
    <aside className="fixed top-0 left-0 h-screen w-[280px] bg-gradient-to-b from-primary to-primary-dark text-white z-50 shadow-xl overflow-y-auto">
      <div className="p-6 border-b border-white/10">
        <div className="mb-4">
          <img src="/img/logo.png" alt="ロゴ" className="w-full object-cover" />
        </div>
        <h1 className="text-xl font-bold text-white text-center leading-tight">
          ガス開閉栓<br />予約管理システム
        </h1>
      </div>
      
      <nav className="py-4">
        <div className="mb-8">
          <div className="text-xs font-semibold text-white/70 uppercase tracking-wide px-5 mb-3">
            メイン機能
          </div>
          <div className="mb-1">
            <a
              href="#"
              className={`flex items-center px-5 py-3 text-white/90 transition-all relative ${
                isActive('all') ? 'bg-white/10 text-white' : 'hover:bg-white/10 hover:text-white'
              }`}
              onClick={handleAllBookingsClick}
            >
              {isActive('all') && (
                <div className="absolute left-0 top-0 bottom-0 w-1 bg-accent" />
              )}
              <svg className="w-5 h-5 mr-3 opacity-80" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
              </svg>
              <span className="font-medium flex-1">予約申請一覧</span>
              <span className="bg-accent text-white px-2 py-0.5 rounded-full text-xs font-semibold min-w-[20px] text-center">
                {totalBookings}
              </span>
            </a>
          </div>
          <div className="mb-1">
            <a
              href="#"
              className={`flex items-center px-5 py-3 text-white/90 transition-all relative ${
                isActive('call_scheduled') ? 'bg-white/10 text-white' : 'hover:bg-white/10 hover:text-white'
              }`}
              onClick={(e) => {
                e.preventDefault()
                handleNavigate('/?status=callout')
              }}
            >
              {isActive('call_scheduled') && (
                <div className="absolute left-0 top-0 bottom-0 w-1 bg-amber-500" />
              )}
              <svg className="w-5 h-5 mr-3 opacity-80" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
              </svg>
              <span className="font-medium flex-1">架電リスト</span>
              <span className="bg-amber-500 text-white px-2 py-0.5 rounded-full text-xs font-semibold min-w-[20px] text-center">
                {callScheduled}
              </span>
            </a>
          </div>
          <div className="mb-1">
            <a
              href="/main/blackout-dates"
              className="flex items-center px-5 py-3 text-white/90 transition-all hover:bg-white/10 hover:text-white"
            >
              <svg className="w-5 h-5 mr-3 opacity-80" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <span className="font-medium flex-1">予約日設定カレンダー</span>
            </a>
          </div>
        </div>
      </nav>

      {/* ユーザー情報・ログアウト */}
      <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-white/10 bg-primary-dark/50">
        <div className="flex items-center gap-3 mb-3 px-1">
          <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center text-white font-semibold text-sm">
            {user?.name?.charAt(0) || 'U'}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-white font-medium text-sm truncate">{user?.name || 'ユーザー'}</p>
            <p className="text-white/60 text-xs truncate">{user?.email || ''}</p>
          </div>
        </div>
        <button
          onClick={handleLogout}
          disabled={isLoggingOut}
          className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-white/10 hover:bg-white/20 text-white/90 hover:text-white rounded-lg transition-all text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isLoggingOut ? (
            <>
              <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              <span>ログアウト中...</span>
            </>
          ) : (
            <>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              <span>ログアウト</span>
            </>
          )}
        </button>
      </div>
    </aside>
  )
}

export default Sidebar
